// ─── Alert Type Configuration ─────────────────────────────────

import type { AlertType, TicketPriority, BleButtonTapAction, BleButtonTapActions } from './types';

/** Default B10 tap→action map applied to new profiles. */
export const DEFAULT_BLE_BUTTON_TAP_ACTIONS: BleButtonTapActions = {
  single: 'SOS',
  double: 'FIRE',
  triple: 'MEDICAL',
};

/** Display metadata for each selectable B10 tap action. */
export const BLE_BUTTON_TAP_ACTION_CONFIG: Record<BleButtonTapAction, {
  label: string;
  color: string;
  icon: string;
}> = {
  SOS:     { label: 'SOS · Pánico', color: '#DC2626', icon: 'alert-circle' },
  FIRE:    { label: 'Incendio',     color: '#EA580C', icon: 'alert-triangle' },
  MEDICAL: { label: 'Médica',       color: '#2563EB', icon: 'plus-circle' },
  NONE:    { label: 'Ninguna',      color: '#6B7280', icon: 'slash' },
};

export const ALERT_CONFIG: Record<AlertType, {
  label: string;
  priority: TicketPriority;
  color: string;
  icon: string;
  autoAssign: boolean;
}> = {
  SOS: {
    label: 'SOS - Emergencia',
    priority: 'CRITICAL',
    color: '#DC2626',
    icon: 'alert-circle',
    autoAssign: true,
  },
  FIRE: {
    label: 'Incendio',
    priority: 'HIGH',
    color: '#EA580C',
    icon: 'flame',
    autoAssign: true,
  },
  MEDICAL: {
    label: 'Emergencia Médica',
    priority: 'HIGH',
    color: '#2563EB',
    icon: 'medkit',
    autoAssign: true,
  },
  CALL: {
    label: 'Llamada',
    priority: 'MEDIUM',
    color: '#7C3AED',
    icon: 'call',
    autoAssign: false,
  },
  PANIC: {
    label: 'Botón de Pánico',
    priority: 'CRITICAL',
    color: '#DC2626',
    icon: 'alert-triangle',
    autoAssign: true,
  },
  INTRUSION: {
    label: 'Intrusión',
    priority: 'CRITICAL',
    color: '#9333EA',
    icon: 'shield-alert',
    autoAssign: true,
  },
  DURESS: {
    label: 'Coacción',
    priority: 'CRITICAL',
    color: '#B91C1C',
    icon: 'user-x',
    autoAssign: true,
  },
  TAMPER: {
    label: 'Sabotaje',
    priority: 'HIGH',
    color: '#D97706',
    icon: 'wrench',
    autoAssign: false,
  },
} as const;

// ─── Ticket Status Labels ─────────────────────────────────────

export const TICKET_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  ASSIGNED: 'Asignada',
  EN_ROUTE: 'En Camino',
  ON_SCENE: 'En Sitio',
  IN_PROGRESS: 'En Progreso',
  // Operator wording — "Finalizada" is the customer-facing term used
  // throughout the UI; the underlying enum stays as RESOLVED.
  RESOLVED: 'Finalizada',
  CLOSED: 'Cerrada',
  ESCALATED: 'Escalada',
  CANCELLED: 'Cancelada',
} as const;

// ─── Patrol Status Labels ─────────────────────────────────────

export const PATROL_STATUS_LABELS: Record<string, string> = {
  AVAILABLE: 'Disponible',
  BACKGROUND: 'Segundo plano',
  ASSIGNED: 'Asignado',
  EN_ROUTE: 'En Camino',
  ON_SCENE: 'En Escena',
  OFF_DUTY: 'Fuera de Servicio',
  OFFLINE: 'Desconectado',
} as const;

// ─── Geofence Type Labels ─────────────────────────────────────

export const GEOFENCE_TYPE_LABELS: Record<string, string> = {
  SAFE: 'Zona Segura',
  DANGER: 'Zona de Peligro',
  RESTRICTED: 'Zona Restringida',
  MONITORED: 'Zona Monitoreada',
} as const;

// ─── Camera Status Labels ─────────────────────────────────────

export const CAMERA_STATUS_LABELS: Record<string, string> = {
  ONLINE: 'En línea',
  OFFLINE: 'Desconectada',
  ERROR: 'Error',
  MAINTENANCE: 'Mantenimiento',
} as const;

export const CAMERA_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ONLINE: { label: 'En línea', color: '#16A34A' },
  OFFLINE: { label: 'Desconectada', color: '#6B7280' },
  ERROR: { label: 'Error', color: '#DC2626' },
  MAINTENANCE: { label: 'Mantenimiento', color: '#EAB308' },
} as const;

export const CAMERA_PROTOCOL_LABELS: Record<string, string> = {
  ONVIF: 'ONVIF',
  HIKVISION_ISAPI: 'Hikvision ISAPI',
  HIKVISION_HCT: 'Hikvision Cloud',
  RTSP_DIRECT: 'RTSP Directo',
} as const;

export const CAMERA_SOURCE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  GATEWAY: { label: 'Gateway', color: '#16A34A', bgColor: '#DCFCE7' }, // Green
  HCT:     { label: 'HCT',     color: '#0284C7', bgColor: '#E0F2FE' }, // Sky blue
  MANUAL:  { label: 'Manual',  color: '#6B7280', bgColor: '#F3F4F6' }, // Gray
} as const;

export const CAMERA_DEVICE_TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  standalone: { label: 'Cámara', icon: 'Video' },
  nvr_channel: { label: 'NVR', icon: 'Server' },
} as const;

// ─── Plan Configuration ──────────────────────────────────────

export const PLAN_CONFIG = {
  BASIC: { label: 'Plan Básico', maxCameras: 2, maxGeofences: 3 },
  PRO: { label: 'Plan Pro', maxCameras: 10, maxGeofences: 10 },
  ELITE: { label: 'Plan Elite', maxCameras: 50, maxGeofences: 50 },
} as const;

// ─── Supabase Tables ──────────────────────────────────────────

export const TABLES = {
  PROFILES: 'profiles',
  TICKETS: 'tickets',
  EVIDENCE: 'evidence',
  CALLS: 'calls',
  TICKET_LOGS: 'ticket_logs',
  PATROL_LOCATIONS: 'patrol_locations',
  GEOFENCES: 'geofences',
  CAMERAS: 'cameras',
  SUBSCRIPTIONS: 'subscriptions',
  PAYMENTS: 'payments',
} as const;

// ─── Storage Buckets ──────────────────────────────────────────

export const STORAGE_BUCKETS = {
  EVIDENCE: 'evidence',
  AVATARS: 'avatars',
  RECORDINGS: 'recordings',
} as const;

// ─── Location Config ──────────────────────────────────────────

export const LOCATION_CONFIG = {
  PATROL_UPDATE_INTERVAL_MS: 10000,
  PATROL_DISTANCE_FILTER_M: 20,
  MAX_ASSIGNMENT_RADIUS_KM: 15,
  GUATEMALA_CENTER: { lat: 14.6349, lng: -90.5069 } as const,
} as const;

// ─── Jitsi Config ─────────────────────────────────────────────

export const JITSI_CONFIG = {
  DOMAIN: 'meet.corporacionasi.com',
  ROOM_PREFIX: 'asi-ticket-',
} as const;

// ─── QPay Config ──────────────────────────────────────────────

export const QPAY_CONFIG = {
  CURRENCY: 'GTQ',
  COUNTRY: 'GT',
} as const;

// ─── Alarm Panel Status Config ────────────────────────────────

export const ALARM_PANEL_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  ONLINE: { label: 'En línea', color: '#16A34A', bgColor: '#DCFCE7' },
  OFFLINE: { label: 'Desconectado', color: '#6B7280', bgColor: '#F3F4F6' },
  ALARM: { label: 'En alarma', color: '#DC2626', bgColor: '#FEE2E2' },
  FAULT: { label: 'Falla', color: '#EAB308', bgColor: '#FEF9C3' },
} as const;

export const ALARM_ZONE_TYPE_CONFIG: Record<string, { label: string; icon: string }> = {
  INSTANT: { label: 'Instantánea', icon: 'Zap' },
  DELAY: { label: 'Con retardo', icon: 'Clock' },
  INTERIOR: { label: 'Interior', icon: 'Home' },
  PERIMETER: { label: 'Perimetral', icon: 'Square' },
  FIRE: { label: 'Incendio', icon: 'Flame' },
  MEDICAL: { label: 'Médica', icon: 'Heart' },
  '24HR': { label: '24 Horas', icon: 'ShieldAlert' },
  OTHER: { label: 'Otra', icon: 'HelpCircle' },
} as const;

export const ALARM_ZONE_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  NORMAL: { label: 'Normal', color: '#16A34A', bgColor: '#DCFCE7' },
  ALARM: { label: 'Alarma', color: '#DC2626', bgColor: '#FEE2E2' },
  TAMPER: { label: 'Sabotaje', color: '#EA580C', bgColor: '#FFEDD5' },
  BYPASS: { label: 'Anulada', color: '#6B7280', bgColor: '#F3F4F6' },
  FAULT: { label: 'Falla', color: '#EAB308', bgColor: '#FEF9C3' },
  OFFLINE: { label: 'Desconectada', color: '#6B7280', bgColor: '#F3F4F6' },
} as const;

export const ALARM_EVENT_SEVERITY_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: string }> = {
  INFO: { label: 'Info', color: '#3B82F6', bgColor: '#DBEAFE', icon: 'Info' },
  WARNING: { label: 'Advertencia', color: '#EAB308', bgColor: '#FEF9C3', icon: 'AlertTriangle' },
  ALARM: { label: 'Alarma', color: '#EA580C', bgColor: '#FFEDD5', icon: 'Bell' },
  CRITICAL: { label: 'Crítico', color: '#DC2626', bgColor: '#FEE2E2', icon: 'AlertOctagon' },
} as const;

export const ALARM_EVENT_CATEGORY_CONFIG: Record<string, { label: string; icon: string }> = {
  burglary: { label: 'Robo', icon: 'ShieldAlert' },
  fire: { label: 'Incendio', icon: 'Flame' },
  medical: { label: 'Médica', icon: 'Heart' },
  panic: { label: 'Pánico', icon: 'AlertCircle' },
  tamper: { label: 'Sabotaje', icon: 'AlertTriangle' },
  system: { label: 'Sistema', icon: 'Settings' },
  arming: { label: 'Armado', icon: 'Shield' },
  test: { label: 'Prueba', icon: 'TestTube' },
} as const;
